import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogContent, Stack, TextField } from '@mui/material';
import type { ButtonProps, SxProps, Theme } from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR } from '~/consts/roles';
import stores from '~/consts/stores';

import UsersService from '~/services/UsersService';

import getErrorMessage from '~/utils/getErrorMessage';

type Props = {
  user: App.User;
  tenant: App.Tenant;
  buttonVariant?: ButtonProps['variant'];
  buttonSx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
  containerSpacing?: number;
  callCentreIcon?: React.ReactNode;
  storeIcon?: React.ReactNode;
};

export const ALLOWED_BRANDS = ['luxuryescapes', 'leagenthub', 'leagenthub_cvfr'];

export default function SpoofUserBlock({
  user,
  tenant,
  buttonVariant = 'contained',
  buttonSx,
  containerSx,
  containerSpacing = 2,
  callCentreIcon,
  storeIcon,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [spoofUrl, setSpoofUrl] = useState<string | null>(null);
  const [forStore, setForStore] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const [storeName, setStoreName] = useState('');

  const flushSpoofingState = () => {
    setSpoofUrl(null);
    setForStore(false);
    setStoreName('');
  };

  const handleSpoofUser = async () => {
    try {
      setProcessing(true);

      const spoofUrl = await UsersService.spoofUser(user.id_member, tenant);

      navigator.clipboard.writeText(spoofUrl);

      setSpoofUrl(spoofUrl);
      setForStore(false);
      setStoreName('');
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to generate spoof link.'), { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  const handleSpoofUserStore = async (utmMedium: string, storeName: string) => {
    try {
      setProcessing(true);

      const spoofUrl = await UsersService.spoofUserStore(user.id_member, tenant, '', utmMedium);

      navigator.clipboard.writeText(spoofUrl);

      setSpoofUrl(spoofUrl);
      setForStore(true);
      setStoreName(storeName);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to generate spoof link.'), { variant: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={containerSpacing} sx={containerSx}>
        <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_EXPERIENCES_COORDINATOR]}>
          <Button
            variant={buttonVariant}
            color="success"
            startIcon={callCentreIcon}
            onClick={handleSpoofUser}
            disabled={isProcessing}
            sx={buttonSx}
          >
            Call Centre
          </Button>
        </PermissionedComponent>

        {ALLOWED_BRANDS.includes(tenant.brand) &&
          Object.entries(stores)
            .filter(([, store]) => store.spoofable)
            .map(([storeKey, store]) => (
              <Button
                key={storeKey}
                variant={buttonVariant}
                color="secondary"
                startIcon={storeIcon}
                onClick={() => handleSpoofUserStore(store.utmMedium, store.name)}
                disabled={isProcessing}
                sx={buttonSx}
              >
                {store.displayName}
              </Button>
            ))}
      </Stack>
      {spoofUrl && (
        <Dialog open onClose={flushSpoofingState}>
          <DialogContent>
            <Stack direction="column" spacing={2} sx={{ my: 2 }}>
              <Alert severity="warning" onClose={flushSpoofingState}>
                Extreme caution, paste into incognito only
              </Alert>
              <Alert severity="info" onClose={flushSpoofingState}>
                Your link has been copied to the clipboard!
              </Alert>
              <TextField
                InputLabelProps={{ shrink: true }}
                label={
                  forStore
                    ? `${storeName} store mode spoofing link for ${user.fullName}`
                    : `Spoofing link for ${user.email}`
                }
                defaultValue={spoofUrl}
                multiline
                disabled
              />
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
